import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Trash2, CheckCircle, Flag, ExternalLink } from "lucide-react";
import adminApi from "@/lib/adminApi";
import { Input } from "@/components/ui/input";
import { Avatar, UserBadges, timeAgo } from "@/components/comments/CommentMeta";

export default function CommentsModerationTab() {
  const [reported, setReported] = useState(true);
  const [search, setSearch] = useState("");
  const [comments, setComments] = useState([]);

  const load = useCallback(async () => {
    const res = await adminApi.get("/admin/comments", { params: { reported, search } });
    setComments(res.data);
  }, [reported, search]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const remove = async (id) => {
    if (!window.confirm("¿Borrar este comentario y sus respuestas?")) return;
    await adminApi.delete(`/admin/comments/${id}`);
    toast.success("Comentario borrado");
    load();
  };

  const dismiss = async (id) => {
    await adminApi.post(`/admin/comments/${id}/dismiss-reports`);
    toast.success("Reportes descartados");
    load();
  };

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-4 md:p-6 shadow-sm" data-testid="admin-comments-tab">
      <div className="flex flex-col md:flex-row md:items-center gap-3 mb-4">
        <div className="flex bg-gray-100 rounded-full p-1 w-fit">
          {[[true, "Reportados"], [false, "Todos"]].map(([val, label]) => (
            <button key={label} onClick={() => setReported(val)} className={`text-sm font-medium px-4 py-1.5 rounded-full ${reported === val ? "bg-black text-white" : "text-gray-600"}`} data-testid={`admin-comments-filter-${val ? "reported" : "all"}`}>
              {label}
            </button>
          ))}
        </div>
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar texto..." className="md:max-w-xs bg-gray-50 border-gray-200 text-black" data-testid="admin-comments-search" />
      </div>
      {comments.length === 0 ? (
        <p className="text-gray-500 text-sm py-8 text-center" data-testid="admin-comments-empty">{reported ? "No hay comentarios reportados 🎉" : "No hay comentarios"}</p>
      ) : (
        <div className="divide-y divide-gray-100">
          {comments.map((c) => (
            <div key={c.id} className="py-3 flex gap-3" data-testid={`admin-comment-${c.id}`}>
              <Avatar author={c.author} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap text-sm">
                  <span className="font-bold text-black">{c.author.name}</span>
                  <UserBadges author={c.author} />
                  {c.author.banned && <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-red-100 text-red-600">Bloqueado</span>}
                  <span className="text-xs text-gray-400">{timeAgo(c.created_at)}</span>
                  {c.report_count > 0 && (
                    <span className="flex items-center gap-1 text-xs text-red-600 font-bold"><Flag className="w-3 h-3" /> {c.report_count}</span>
                  )}
                </div>
                <p className="text-gray-800 text-sm mt-1 whitespace-pre-wrap break-words">{c.content}</p>
                <a href={`/video/${c.video_id}`} target="_blank" rel="noopener noreferrer" className="text-xs text-[#E91E8C] flex items-center gap-1 mt-1">
                  {c.video_title || "Ver video"} <ExternalLink className="w-3 h-3" />
                </a>
              </div>
              <div className="flex flex-col gap-2">
                <button onClick={() => remove(c.id)} className="p-2 rounded-lg text-red-600 hover:bg-red-50" title="Borrar" data-testid={`admin-comment-delete-${c.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
                {c.report_count > 0 && (
                  <button onClick={() => dismiss(c.id)} className="p-2 rounded-lg text-green-600 hover:bg-green-50" title="Descartar reportes" data-testid={`admin-comment-dismiss-${c.id}`}>
                    <CheckCircle className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
